import React, { useState, useEffect, useRef } from "react";
import "./CardPreview.less";
import { CardFront } from "./CardFront";
import { CardBack } from "./CardBack";
import useCardDimensions from "./hooks/useCardDimensions";
import useCardAnimation from "./hooks/useCardAnimation";
import { BASE_CONTAINER_WIDTH } from "./cardPreviewConfig";

// Status type for better type checking and reusability
export type CardStatus =
  | "photo-pending"
  | "photo-review"
  | "photo-rejected"
  | "questionnaire"
  | "sent"
  | "default";

interface CardPreviewProps {
  imageUrl?: string;
  status?: CardStatus;
  surveyCompleted?: boolean;
  containerWidth?: number;
  // Custom configuration options
  animationConfig?: {
    duration?: number;
    timingFunction?: string;
  };
  renderConfetti?: boolean;
  onAnimationComplete?: () => void;
}

/**
 * Interactive card preview component that handles animations and state transitions
 *
 * This component manages:
 * - Card front/back display with image positioning
 * - Complex animations between different application states
 * - Responsive sizing and positioning based on screen dimensions
 * - Automatic image dimension detection and card sizing
 */
export const CardPreview: React.FC<CardPreviewProps> = ({
  imageUrl,
  status = "default",
  surveyCompleted = false,
  containerWidth = BASE_CONTAINER_WIDTH,
  animationConfig,
  renderConfetti = true,
  onAnimationComplete
}) => {
  const [showCardBack, setShowCardBack] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Refs for animation
  const cardFrontRef = useRef<HTMLDivElement>(null);
  const cardBackRef = useRef<HTMLDivElement>(null);
  const confettiRef = useRef<HTMLDivElement>(null);
  const whooshRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Determine if card back can be shown
  const canShowCardBack = status !== "sent" || surveyCompleted;

  // Use custom hooks for dimensions and animations
  const { cardHeight, cardDimensions, handleImageLoad } = useCardDimensions({
    containerWidth,
    imageUrl,
    imageRef
  });

  // Setup animation with refs and configuration
  useCardAnimation({
    status,
    showCardBack,
    surveyCompleted,
    cardDimensions,
    containerWidth,
    imageUrl,
    imageLoaded,
    refs: {
      frontRef: cardFrontRef,
      backRef: cardBackRef,
      confettiRef: confettiRef,
      whooshRef: whooshRef
    },
    config: {
      duration: animationConfig?.duration,
      timingFunction: animationConfig?.timingFunction,
      onComplete: onAnimationComplete
    }
  });

  // Handle image load response from CardFront
  const handleImageLoaded = (dimensions: { width: number; height: number }) => {
    const newDimensions = handleImageLoad(dimensions);

    // Set image loaded state
    setImageLoaded(true);

    // Show the card back if allowed and dimensions are calculated
    if (canShowCardBack && newDimensions.width > 0 && newDimensions.height > 0) {
      setShowCardBack(true);
    }
  };

  // Update showCardBack when surveyCompleted or status changes
  useEffect(() => {
    if (canShowCardBack && cardDimensions.width > 0 && cardDimensions.height > 0) {
      setShowCardBack(true);
    } else {
      setShowCardBack(false);
    }
  }, [canShowCardBack, cardDimensions]);

  // Update when image URL changes - reset card back state
  useEffect(() => {
    if (!imageUrl && canShowCardBack && cardDimensions.width > 0 && cardDimensions.height > 0) {
      setShowCardBack(true);
    } else if (imageUrl) {
      setShowCardBack(false);
      // Reset image loaded state on image URL changes
      setImageLoaded(false);

      // If the image already exists in the cache, it might not trigger onLoad
      // Check if image has completed loading immediately
      if (imageRef.current && imageRef.current.complete) {
        // For cached images, manually trigger the loaded state
        setImageLoaded(true);
      }
    }
  }, [imageUrl, canShowCardBack, cardDimensions, setImageLoaded, imageRef]);

  return (
    <div className="card-preview">
      {renderConfetti && <div className="card-preview__confetti" ref={confettiRef}></div>}
      <div className="card-preview__whoosh" ref={whooshRef}></div>
      <CardFront
        ref={cardFrontRef}
        imageUrl={imageUrl}
        height={cardHeight}
        onImageLoad={handleImageLoaded}
        imageRef={imageRef}
      />
      <CardBack
        visible={showCardBack}
        ref={cardBackRef}
        width={cardDimensions.width}
        height={cardDimensions.height}
        status={status}
      />
    </div>
  );
};
